import { useEffect, useState } from 'react'
import { ImageIcon, Upload, ArrowLeft, CheckCircle2, XCircle, Clock, Trash2, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import ImagecompoNav from '@/components/ImagecompoNav'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Link } from 'react-router-dom'
import { imagecompoAPI } from '../lib/api'
import PageHeader from '../components/PageHeader'
import { useImagecompoSocket } from '../lib/imagecompoSocket'
import { useConfirm } from '../hooks/useConfirm'

export default function ImageCompoInleverPage() {
  const confirm = useConfirm()
  const [data, setData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const load = () => imagecompoAPI.getMySubmission().then(r => setData(r.data)).catch(() => {})
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const show = data?.show
  const sub = data?.submission
  const canSubmit = show?.submissions_open

  if (!show) return (
    <div className="max-w-3xl mx-auto">
      <ImagecompoNav />
      <PageHeader icon={ImageIcon}  title="Inschrijven" />
      <Card><CardContent className="p-6 text-center text-muted-foreground">Nog geen actieve show.</CardContent></Card>
    </div>
  )

  const hasInscription = !!sub && sub.status !== 'withdrawn'
  const step1Done = hasInscription
  const step2Done = hasInscription && sub.title && sub.image_url

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <ImagecompoNav />
      <PageHeader
        icon={ImageIcon}

        title={show.name}
        subtitle={show.edition || 'AI Image Compo'}
        actions={<Button asChild variant="outline"><Link to="/imagecompo"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>}
      />

      {!canSubmit && !hasInscription && (
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Inschrijvingen zijn (nog) niet geopend voor deze show.
        </CardContent></Card>
      )}

      {/* Voortgang steps */}
      {(canSubmit || hasInscription) && (
        <div className="flex items-center gap-3">
          <StepDot done={step1Done} label="1. Inschrijven" />
          <div className="flex-1 h-0.5 bg-border" />
          <StepDot done={step2Done} label="2. Beeld indienen" />
          <div className="flex-1 h-0.5 bg-border" />
          <StepDot done={sub?.status === 'approved'} label="3. Goedgekeurd" pending={sub?.status === 'pending' && step2Done} />
        </div>
      )}

      {err && <div className="p-3 bg-red-500/10 border border-red-500/40 rounded text-sm text-red-400">{err}</div>}

      {/* Status card */}
      {sub && (
        <StatusCard sub={sub} onWithdraw={async () => {
          if (!(await confirm('Zeker weten dat je je inzending wil intrekken?'))) return
          setSaving(true); setErr('')
          try { await imagecompoAPI.withdrawMySubmission(); await load() }
          catch (e) { setErr(e.response?.data?.error || e.message) }
          finally { setSaving(false) }
        }} disabled={saving} />
      )}

      {/* Step 1: inschrijven */}
      {!hasInscription && canSubmit && (
        <InscriptionForm onSubmit={async (payload) => {
          setSaving(true); setErr('')
          try { await imagecompoAPI.createMySubmission(payload); await load() }
          catch (e) { setErr(e.response?.data?.error || e.message) }
          finally { setSaving(false) }
        }} disabled={saving} />
      )}

      {/* Step 2: image + title */}
      {hasInscription && sub.status !== 'withdrawn' && (
        <ImageForm sub={sub} onSave={async (patch) => {
          setSaving(true); setErr('')
          try { await imagecompoAPI.updateMySubmission(patch); await load() }
          catch (e) { setErr(e.response?.data?.error || e.message) }
          finally { setSaving(false) }
        }} onUpload={async (file) => {
          setSaving(true); setErr('')
          try { await imagecompoAPI.uploadMyImage(file); await load() }
          catch (e) { setErr(e.response?.data?.error || e.message) }
          finally { setSaving(false) }
        }} disabled={saving} />
      )}
    </div>
  )
}

function StepDot({ done, pending, label }) {
  const color = done ? 'bg-emerald-500 border-emerald-500' :
                pending ? 'bg-yellow-500 border-yellow-500 animate-pulse' :
                'bg-transparent border-border'
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center ${color}`}>
        {done ? <CheckCircle2 className="h-5 w-5 text-white" /> :
         pending ? <Clock className="h-4 w-4 text-white" /> : null}
      </div>
      <div className="text-xs text-muted-foreground text-center whitespace-nowrap">{label}</div>
    </div>
  )
}

function StatusCard({ sub, onWithdraw, disabled }) {
  const border = sub.status === 'approved' ? 'border-emerald-500/40 bg-emerald-500/5'
    : sub.status === 'rejected' ? 'border-red-500/40 bg-red-500/5'
    : sub.status === 'withdrawn' ? 'border-slate-500/40 bg-slate-500/5'
    : 'border-yellow-500/40 bg-yellow-500/5'
  const label = sub.status === 'approved' ? 'Goedgekeurd — je zit in de show'
    : sub.status === 'rejected' ? 'Afgekeurd'
    : sub.status === 'withdrawn' ? 'Ingetrokken'
    : (sub.title && sub.image_url) ? 'Wacht op goedkeuring van admin' : 'In behandeling'
  return (
    <Card className={border}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest font-semibold">{label}</div>
          <div className="font-bold truncate">{sub.title || sub.proposed_team_name}</div>
        </div>
        {sub.status !== 'withdrawn' && (
          <Button variant="outline" size="sm" onClick={onWithdraw} disabled={disabled}>
            <Trash2 className="h-4 w-4 mr-1" />Intrekken
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function InscriptionForm({ onSubmit, disabled }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [colors, setColors] = useState('#8b3fff, #ff2b8f')
  return (
    <Card>
      <CardHeader><CardTitle>Stap 1 — team + inschrijving</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Team naam</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Neon Wolves" />
          </div>
          <div>
            <Label>Team code (3 letters)</Label>
            <Input value={code} maxLength={4} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="NEW" />
          </div>
        </div>
        <div>
          <Label>Vlag kleuren (komma-gescheiden)</Label>
          <Input value={colors} onChange={e => setColors(e.target.value)} placeholder="#8b3fff, #ff2b8f" />
        </div>
        <Button size="lg" disabled={disabled || !name} onClick={() => {
          const flag_colors = colors.split(',').map(c => c.trim()).filter(Boolean)
          onSubmit({ proposed_team_name: name, proposed_team_code: code || null, proposed_flag_colors: flag_colors })
        }}>
          {disabled ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Inschrijven
        </Button>
      </CardContent>
    </Card>
  )
}

function ImageForm({ sub, onSave, onUpload, disabled }) {
  const [title, setTitle] = useState(sub.title || '')
  const [description, setDescription] = useState(sub.description || '')
  const [file, setFile] = useState(null)
  return (
    <Card>
      <CardHeader><CardTitle>Stap 2 — jouw beeld</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Titel</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijvoorbeeld: 'Cyberpunk wolven in de sneeuw'" />
        </div>
        <div>
          <Label>Beschrijving / prompt (optioneel)</Label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                    placeholder="Prompt of context bij je beeld" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={disabled} onClick={() => onSave({ title, description })}>
            Titel + beschrijving opslaan
          </Button>
        </div>

        <div className="border-t border-border pt-4">
          <Label>Image bestand (PNG / JPG / WEBP, max 25MB)</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
            <Button disabled={disabled || !file} onClick={() => file && onUpload(file)}>
              <Upload className="h-4 w-4 mr-2" />Upload
            </Button>
          </div>
        </div>

        {sub.image_url && (
          <div>
            <Label className="text-xs text-muted-foreground">Huidige image</Label>
            <img src={sub.image_url} alt="" className="w-full max-h-96 object-contain rounded border border-border mt-1" />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
